import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { PortableText } from '@portabletext/react'
import Section from '../components/shared/Section'
import SEO from '../components/shared/SEO'
import { getPostBySlug, urlFor } from '../lib/sanity'
import { formatDate } from '../lib/utils'
import { portableTextComponents } from '../components/shared/PortableTextComponents'
import { ORG_SCHEMA, PERSON_SCHEMA, SITE_URL, withContext } from '../lib/seo'
import { ArrowLeft } from 'lucide-react'

export default function BlogPost() {
  const { slug } = useParams()
  const [post, setPost] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (slug) {
      getPostBySlug(slug)
        .then((data) => {
          setPost(data)
          setLoading(false)
        })
        .catch((error) => {
          console.error('Error fetching post:', error)
          setLoading(false)
        })
    }
  }, [slug])

  if (loading) {
    return (
      <Section>
        <div className="text-center text-neutral-400">
          Loading...
        </div>
      </Section>
    )
  }

  if (!post) {
    return (
      <Section>
        <div className="text-center">
          <h1 className="text-4xl text-neon-pink mb-4">
            Post not found
          </h1>
          <Link
            to="/blog"
            className="text-neon-lime hover:text-neon-yellow inline-flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Back to blog
          </Link>
        </div>
      </Section>
    )
  }

  const postUrl = `${SITE_URL}/blog/${post.slug?.current || ''}`
  const postImage = post.mainImage
    ? urlFor(post.mainImage).width(1200).height(630).fit('crop').url()
    : undefined
  const seoDescription =
    post.subtitle || post.excerpt || `${post.title} — Mathos Consult`

  return (
    <>
      <SEO
        title={`${post.title} — Mathos Consult`}
        description={seoDescription}
        type="article"
        image={postImage}
        jsonLd={withContext([
          {
            '@type': 'BlogPosting',
            '@id': `${postUrl}#article`,
            headline: post.title,
            description: seoDescription,
            datePublished: post.publishedAt,
            author: { '@id': `${SITE_URL}/#alexander` },
            publisher: { '@id': `${SITE_URL}/#organization` },
            mainEntityOfPage: { '@type': 'WebPage', '@id': postUrl },
            url: postUrl,
            ...(postImage ? { image: postImage } : {}),
          },
          PERSON_SCHEMA,
          ORG_SCHEMA,
        ])}
      />

      {/* Back to Blog */}
      <Section padding="small">
        <Link
          to="/blog"
          className="text-neon-lime hover:text-neon-yellow inline-flex items-center gap-2"
        >
          <ArrowLeft size={20} />
          Back to blog
        </Link>
      </Section>

      {/* Article Header */}
      <Section padding="none">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl text-neon-orange mb-4">
            {post.title}
          </h1>
          {post.subtitle && (
            <p className="text-xl md:text-2xl text-neutral-300 mb-6 leading-snug">
              {post.subtitle}
            </p>
          )}
          <div className="flex items-center justify-center gap-4 text-neutral-400 mb-8">
            <span>{formatDate(post.publishedAt)}</span>
            {post.readTime && (
              <>
                <span>•</span>
                <span>{post.readTime} min read</span>
              </>
            )}
          </div>
          {post.categories && post.categories.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {post.categories.map((category, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-neon-pink/10 text-neon-pink rounded-full text-sm border border-neon-pink/30"
                >
                  {category}
                </span>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* Article Content */}
      <Section>
        <div className="max-w-3xl mx-auto">
          {post.mainImage && (
            <div className="mb-12 rounded-xl overflow-hidden">
              <img
                src={post.mainImage}
                alt={post.title}
                className="w-full h-auto"
              />
            </div>
          )}

          <article className="prose prose-lg max-w-none">
            {post.body && (
              <PortableText
                value={post.body}
                components={portableTextComponents}
              />
            )}
          </article>
        </div>
      </Section>

      {/* Author Section */}
      <Section background="gray">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-neon-lime/20 border-2 border-neon-lime flex items-center justify-center text-neon-lime text-2xl font-bold flex-shrink-0">
              M
            </div>
            <div>
              <h3 className="text-xl text-neon-lime mb-2">
                About the Author
              </h3>
              <p className="text-neutral-400 mb-4">
                Strategic service and systems designer combining operational rigour, human-centred design, and business thinking to help organisations align products, services, and operations around real human needs.
              </p>
              <Link
                to="/about"
                className="text-neon-pink hover:text-neon-purple font-medium"
              >
                Learn more →
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </>
  )
}
